import { isChildNode } from 'module/helpers/is-child-node';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { Popover } from 'module/ui/components/atoms/popover';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FilterButton } from 'components/common/styles';
import { FilterPopoverFooter } from './done-button';

const FilterBoxWrapper = styled.div`
  padding: var(--spacing-04) var(--spacing-05);
`;

export interface Props {
  loading: boolean;
  reset: () => void;
  onVisibilityChange?: (visible: boolean) => void;
  popoverContent: ReactNode;
  popoverTriggerLabel: ReactNode;
  saveViewLabel?: ReactNode;
  showClearButton: boolean;
  onDone?: () => void;
}

export const BaseFilterPopover: FC<React.PropsWithChildren<Props>> = ({
  loading,
  onVisibilityChange,
  reset,
  popoverContent,
  popoverTriggerLabel,
  saveViewLabel,
  showClearButton,
  onDone,
}) => {
  const [visible, setVisible] = useState(false);
  const isChild = isChildNode([
    'filter-box',
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
    'ant-popover-inner-content',
  ]);

  const onApply = useCallback(() => {
    setVisible(false);
    onDone?.();
  }, [onDone]);

  const onCancel = () => {
    setVisible(false);
  };

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  return (
    <>
      <Popover
        content={
          <>
            <FilterBoxWrapper>{popoverContent}</FilterBoxWrapper>
            <FilterPopoverFooter onCancel={onCancel} onApply={onApply} />
          </>
        }
        trigger={['click']}
        placement="bottomLeft"
        open={visible}
        destroyTooltipOnHide
      >
        <OutsideClickHandler allowed={isChild} onOutsideClick={onCancel}>
          <FilterButton
            type="button"
            leftIcon={<FontAwesomeIcon icon={faFilter} size={16} />}
            size="medium"
            data-cy="buttons.filter-button"
            onClick={() => setVisible(!visible)}
            loading={loading}
          >
            {popoverTriggerLabel}
            {saveViewLabel}
          </FilterButton>
        </OutsideClickHandler>
      </Popover>
      {!loading && showClearButton ? (
        <FilterButton
          type="button"
          size="medium"
          leftIcon={<FontAwesomeIcon icon={faClose} size={16} />}
          data-cy="buttons.filter-button"
          onClick={reset}
          loading={loading}
        />
      ) : null}
    </>
  );
};
